import * as React from 'react';

import {Nut_Capabilities} from 'nutshell-core/settings-constants';

import {SaveButton} from 'shells/button/save-button/save-button';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {AiSparkleNewIcon} from 'shells/icon/svg/ai-sparkle-new-icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {LegacySetupTableContainer} from 'shells/table/legacy-setup-table-container';
import {LegacySetupTableWrapper} from 'shells/table/legacy-setup-table-wrapper';

import {useUpdateSetting} from '../../core/use-update-setting';
import {useGetPowerAiSummarizationMonthlyUsage} from '../../power-ai/power-ai-usage-popover/graphql/hooks';
import {useGetAgentMonthlyUsage} from '../../team-ai/graphql/hooks/use-get-agent-monthly-usage';

import {
    AiSummarizationSection,
    AiTranscriptionSection,
    AiEmailRepliesSection,
    AiAgentSection,
} from '.';

export function AiFeaturesPage() {
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [errorSaving, setErrorSaving] = React.useState<boolean>(false);
    const [isSummarizationEnabled, setIsSummarizationEnabled] = React.useState<boolean>(
        NutClientConfig.enableAi
    );
    const [isTranscriptionEnabled, setIsTranscriptionEnabled] = React.useState<boolean>(
        NutClientConfig.enableNotetaker
    );
    const [isEmailRepliesEnabled, setIsEmailRepliesEnabled] = React.useState<boolean>(
        NutClientConfig.enableAiEmailReplies
    );
    const [isTeamAiEnabled, setIsTeamAiEnabled] = React.useState<boolean>(
        NutClientConfig.enableAiAgents
    );

    const {monthlyUsage: summarizationMonthlyUsage, isLoading: isSummarizationLoading} =
        useGetPowerAiSummarizationMonthlyUsage();
    const {limit: summarizationLimit, remaining: summarizationRemaining} =
        summarizationMonthlyUsage || {};

    const {monthlyUsage: teamAiMonthlyUsage, isLoading: isTeamAiLoading} =
        useGetAgentMonthlyUsage();
    const {limit: teamAiLimit, remaining: teamAiRemaining} = teamAiMonthlyUsage || {};

    const updateSetting = useUpdateSetting();

    if (isSummarizationLoading || isTeamAiLoading) {
        return <LoadingPage />;
    }

    const handleSave = () => {
        setIsSaving(true);
        setErrorSaving(false);
        Promise.all([
            updateSetting({
                settingName: Nut_Capabilities.AI_SUMMARY_HIDE_SETTING_NAME,
                value: isSummarizationEnabled ? 0 : 1,
                isGlobal: true,
            }),
            updateSetting({
                settingName: Nut_Capabilities.AI_LIVE_NOTE_TRANSCRIPTION_HIDE_SETTING_NAME,
                value: isTranscriptionEnabled ? 0 : 1,
                isGlobal: true,
            }),
            updateSetting({
                settingName: Nut_Capabilities.AI_EMAIL_REPLIES_HIDE_SETTING_NAME,
                value: isEmailRepliesEnabled ? 0 : 1,
                isGlobal: true,
            }),
            updateSetting({
                settingName: Nut_Capabilities.AI_AGENTS_HIDE_SETTING_NAME,
                value: isTeamAiEnabled ? 0 : 1,
                isGlobal: true,
            }),
        ])
            .then(() => {
                window.location.reload();
            })
            .catch(() => {
                setErrorSaving(true);
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    return (
        <StackLegacy spacing={16}>
            {errorSaving && (
                <FlashBanner
                    type='error'
                    duration={4000}
                    hasBorder={true}
                    onHidden={() => setErrorSaving(false)}
                >
                    Error, settings may not have been saved
                </FlashBanner>
            )}
            <LegacySetupTableWrapper
                title='AI features'
                icon={AiSparkleNewIcon}
                actionItem={
                    <div className='pad-8'>
                        <SaveButton
                            variant='save'
                            size='small'
                            isSaving={isSaving}
                            // @ts-expect-error
                            onClick={handleSave}
                            errorSaving={errorSaving}
                            resetOnComplete={true}
                        />
                    </div>
                }
            >
                <LegacySetupTableContainer overflow='show'>
                    <div className='pad-16'>
                        <StackLegacy spacing={16}>
                            <AiSummarizationSection
                                setIsEnabled={setIsSummarizationEnabled}
                                isEnabled={isSummarizationEnabled}
                                limit={summarizationLimit}
                                remaining={summarizationRemaining}
                            />
                            <AiTranscriptionSection
                                setIsEnabled={setIsTranscriptionEnabled}
                                isEnabled={isTranscriptionEnabled}
                            />
                            <AiEmailRepliesSection
                                setIsEnabled={setIsEmailRepliesEnabled}
                                isEnabled={isEmailRepliesEnabled}
                            />
                            <AiAgentSection
                                setIsEnabled={setIsTeamAiEnabled}
                                isEnabled={isTeamAiEnabled}
                                limit={teamAiLimit}
                                remaining={teamAiRemaining}
                            />
                        </StackLegacy>
                    </div>
                </LegacySetupTableContainer>
            </LegacySetupTableWrapper>
        </StackLegacy>
    );
}
